/**
 * WHAT AN ORDER STATUS MEANS TO THE CUSTOMER — the one classification both
 * checkout screens (CheckoutClient's poll and /complete's poll) read.
 *
 * The two screens used to carry their own copies of TERMINAL/SETTLED lists,
 * and neither listed `pending_acceptance`. That is where an A1 transport order
 * lands once the card is AUTHORIZED (manual capture): the operator still has
 * to accept the seat before anything is charged. Unlisted, it read as "payment
 * still in flight", so the customer watched "Processing your payment…" for 30
 * polls and was then told the payment "may still complete" — for an order that
 * was already exactly where it should be.
 *
 * Unknown statuses still classify as `in_flight`: a status this file has never
 * heard of must keep the poll running and print verbatim, never be guessed
 * into an outcome.
 */

export type OrderPhase =
  /** created, no payment attempt has succeeded — the pay form is correct */
  | "payable"
  /** a payment attempt is (or may be) moving — keep polling */
  | "in_flight"
  /** the last attempt failed; the order is still unpaid and still payable */
  | "payment_failed"
  /** card authorized, NOT charged; waiting on the operator to accept */
  | "awaiting_acceptance"
  /** the literal `paid` — the only status that may print a paid screen */
  | "paid"
  /** a final outcome that is neither paid nor awaiting acceptance */
  | "closed";

const PAYABLE = ["draft", "submitted", "pending_payment"];

/**
 * `invoiced` is written by vendor approval (snowclub-be order.processOrder),
 * not by a payment, so it closes the order without being `paid`.
 */
const CLOSED = ["invoiced", "cancelled", "refunded", "partially_refunded"];

export function orderPhase(status: string | null | undefined): OrderPhase {
  if (!status) return "in_flight";
  if (status === "paid") return "paid";
  if (status === "pending_acceptance") return "awaiting_acceptance";
  if (status === "payment_failed") return "payment_failed";
  if (CLOSED.includes(status)) return "closed";
  if (PAYABLE.includes(status)) return "payable";
  return "in_flight";
}

/** The statuses a customer may be left on with nothing to do — no pay form,
 * no further poll. `payment_failed` is NOT one: it ends an attempt, not the
 * order, and must reach the retry arm. */
export function isSettledStatus(status: string | null | undefined): boolean {
  const phase = orderPhase(status);
  return phase === "paid" || phase === "awaiting_acceptance" || phase === "closed";
}

/** Statuses that end a poll: settled, or a failed attempt. */
export function endsPoll(status: string | null | undefined): boolean {
  return isSettledStatus(status) || orderPhase(status) === "payment_failed";
}

/** The order a no-`?order=` checkout visit may pick up to pay. */
export function isPayableStatus(status: string | null | undefined): boolean {
  return orderPhase(status) === "payable";
}
